<script setup lang="ts">
    import { reactive, inject, onMounted, onUnmounted, computed } from 'vue';
    import { useRoute } from 'vue-router';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ScopeAccess } from '@shared/directives/Scope.access';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { Permissions } from '@shared/Root/Permissions';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import Tabs, { tab } from '@shared/components/Tabs.vue';
    import List from '@shared/components/List/List.vue';
    import IssueTemplateList from './IssueTemplateList.vue';
    import NewPreview from '@shared/components/Editor/NewPreview.vue';
    import {
        getGlobalIssueTemplates,
        getGlobalIssueTemplate,
        getIssue,
        createIssueFromTemplate,
        type GlobalIssueTemplateRef,
        type GlobalIssueTemplate,
        type IssueDetail,
        type NewIssuePayload,
    } from './Issues.access';
    import { updateEventProperty } from '@cwd/Views/Event/Event.access';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);
    const scopeAccess: ScopeAccess = injectorAccess.injector.get(ScopeAccess);
    const route = useRoute();

    const showAllGlobalTemplates = 'all';

    const state = reactive<{
        hasTemplatesScope: boolean | undefined;
        hasIssuesScope: boolean | undefined;
        hideFreeTemplates: boolean | undefined;
        globalTemplates: GlobalIssueTemplateRef[];
        isLoading: boolean;
        currentGlobalTemplateTagId: string;
        globalTemplateSortField: string;
        globalTemplateSortDirection: 'asc' | 'desc';
        issueId: string | null;
        isGlobalTemplate: boolean;
        selectedTabIndex: number;
        previewSrc: string;
        previewBlobUrl: string | null;
        selectedGlobalTemplate: GlobalIssueTemplate | null;
        selectedIssueDetail: IssueDetail | null;
        eventId: string | undefined;
        segmentId: string | undefined;
        surveyId: string | undefined;
        communicationType: number | null;
        isParticipantStatusChangeIssue: boolean;
        isAttestationIssue: boolean;
    }>({
        hasTemplatesScope: undefined,
        hasIssuesScope: undefined,
        hideFreeTemplates: undefined,
        globalTemplates: [],
        isLoading: false,
        currentGlobalTemplateTagId: showAllGlobalTemplates,
        globalTemplateSortField: 'SortOrder',
        globalTemplateSortDirection: 'asc',
        issueId: null,
        isGlobalTemplate: false,
        selectedTabIndex: 0,
        previewSrc: '',
        previewBlobUrl: null,
        selectedGlobalTemplate: null,
        selectedIssueDetail: null,
        eventId: undefined,
        segmentId: undefined,
        surveyId: undefined,
        communicationType: null,
        isParticipantStatusChangeIssue: false,
        isAttestationIssue: false,
    });

    let tabPickedByUser = false;

    onMounted(async () => {
        state.hasTemplatesScope = scopeAccess.hasScope({
            Feature: 'IssueTemplates',
            Permission: Permissions.Read,
        });

        state.hasIssuesScope = scopeAccess.hasScope({
            Feature: 'Issues',
            Permission: Permissions.Write,
        });

        state.hideFreeTemplates = contextAccess.state.currentAccount?.AccountSettings.find((s) => s.Key === 'Email.HideFreeTemplates')?.Value === 'true';

        if (!state.hasTemplatesScope) {
            state.selectedTabIndex = state.hideFreeTemplates ? 2 : 1;
        }

        if (state.hasTemplatesScope && !state.hideFreeTemplates) {
            loadGlobalTemplates();
        }

        // A tab in the url is an explicit choice — never let the default override it.
        if (route.query.t !== undefined) tabPickedByUser = true;

        state.eventId = route.query.eventId as string | undefined;
        state.segmentId = route.query.segmentId as string | undefined;
        state.surveyId = route.query.surveyId as string | undefined;

        const communicationType = route.query.communicationType as string | undefined;
        if (communicationType) {
            const num = parseInt(communicationType, 10);
            if (num > 1) state.communicationType = num;
        }

        state.isParticipantStatusChangeIssue = 'participantStatusChangeIssue' in route.query;
        state.isAttestationIssue = 'attestationIssue' in route.query;
    });

    onUnmounted(() => {
        revokeBlobUrl();
    });

    const loadGlobalTemplates = async () => {
        if (state.isLoading) return;
        state.isLoading = true;

        getGlobalIssueTemplates()
            .then((data) => {
                state.globalTemplates = data.sort((a, b) => (a.SortOrder ?? 0) - (b.SortOrder ?? 0));
            })
            .finally(() => (state.isLoading = false));
    };

    const filteredGlobalTemplates = computed(() => {
        const base =
            state.currentGlobalTemplateTagId === showAllGlobalTemplates
                ? state.globalTemplates
                : state.globalTemplates.filter((item) => item.Tags.find((tag) => tag.TagId === state.currentGlobalTemplateTagId));

        const field = state.globalTemplateSortField as keyof (typeof base)[0];
        const dir = state.globalTemplateSortDirection === 'asc' ? 1 : -1;
        return [...base].sort((a, b) => {
            const av = a[field] ?? '';
            const bv = b[field] ?? '';
            return av < bv ? -dir : av > bv ? dir : 0;
        });
    });

    const updateGlobalTemplateOrder = (field: string, direction: 'asc' | 'desc') => {
        state.globalTemplateSortField = field;
        state.globalTemplateSortDirection = direction;
    };

    const globalTemplateOrderOptions = () => [{ display: $Resources.IssuesSortByTemplateNameLabel, key: 'Name' }];

    const globalTemplateTags = computed(() => {
        const tags: { display: string; key: string }[] = [{ display: $Resources.ToggleOptionsAll, key: showAllGlobalTemplates }];
        const tagKeys = new Set<string>();

        state.globalTemplates.forEach((template) => {
            template.Tags.forEach((tag) => {
                if (!tagKeys.has(tag.TagId)) {
                    tagKeys.add(tag.TagId);
                    tags.push({ display: tag.Title, key: tag.TagId });
                }
            });
        });

        return tags;
    });

    const revokeBlobUrl = () => {
        if (state.previewBlobUrl) {
            URL.revokeObjectURL(state.previewBlobUrl);
            state.previewBlobUrl = null;
        }
    };

    const getHtml = (html: string = '', css: string = '') => {
        return `<!DOCTYPE html>
            <html>
                <head>
                <style>${css}</style>
                </head>
                <body style="margin:0; padding:0;">
                    <div class="canvas">
                        ${html}
                    </div>
                </body>
        </html>`;
    };

    const setIssueId = async (id: string, isGlobalTemplate?: boolean, isAccountTemplate?: boolean) => {
        if (state.isLoading) return;

        const isDeselect = id === state.issueId && (isGlobalTemplate || false) === state.isGlobalTemplate;
        if (isDeselect) {
            closePreview();
            return;
        }

        revokeBlobUrl();
        state.issueId = id;
        state.isGlobalTemplate = isGlobalTemplate || false;
        state.previewSrc = '';
        state.selectedGlobalTemplate = null;
        state.selectedIssueDetail = null;

        state.isLoading = true;

        if (isGlobalTemplate) {
            getGlobalIssueTemplate(id)
                .then((data) => {
                    state.selectedGlobalTemplate = data;
                    const html = getHtml(data.EditableHtml || data.BodyHtml, data.EditableCss);
                    const blob = new Blob([html], { type: 'text/html' });
                    state.previewBlobUrl = URL.createObjectURL(blob);
                    state.previewSrc = state.previewBlobUrl;
                })
                .finally(() => (state.isLoading = false));
        } else if (isAccountTemplate) {
            getIssue(id)
                .then((data) => {
                    state.selectedIssueDetail = data;
                    const html = getHtml(data.EditableHtml || data.BodyHtml, data.EditableCss);
                    const blob = new Blob([html], { type: 'text/html' });
                    state.previewBlobUrl = URL.createObjectURL(blob);
                    state.previewSrc = state.previewBlobUrl;
                })
                .finally(() => (state.isLoading = false));
        } else {
            getIssue(id)
                .then((data) => {
                    state.selectedIssueDetail = data;
                    const html = getHtml(data.EditableHtml || data.BodyHtml, data.EditableCss);
                    const blob = new Blob([html], { type: 'text/html' });
                    state.previewBlobUrl = URL.createObjectURL(blob);
                    state.previewSrc = state.previewBlobUrl;
                })
                .finally(() => (state.isLoading = false));
        }
    };

    const closePreview = () => {
        revokeBlobUrl();
        state.issueId = null;
        state.isGlobalTemplate = false;
        state.previewSrc = '';
        state.selectedGlobalTemplate = null;
        state.selectedIssueDetail = null;
    };

    let tabInitialized = false;
    // The template list emits hasItems once it has loaded, which can land after the user has
    // already picked a tab — applying the default then would yank them out of it.
    const updateCurrentTab = (hasTemplates: boolean) => {
        if (state.hideFreeTemplates || tabInitialized || tabPickedByUser) return;
        tabInitialized = true;
        state.selectedTabIndex = hasTemplates ? 0 : 1;
    };

    const buildSubject = (original: string) => {
        if (!original) return '';
        if (state.eventId) return original;
        const prefix = ($Resources.CopyOfName as string) || '';
        const newSubject = prefix ? prefix + original : original;
        return newSubject.length > 250 ? original : newSubject;
    };

    const startEditing = async () => {
        if (state.isLoading || !state.issueId) return;
        state.isLoading = true;

        let payload: NewIssuePayload;

        if (state.isGlobalTemplate && state.selectedGlobalTemplate) {
            const t = state.selectedGlobalTemplate;
            payload = {
                Subject: buildSubject(t.Name || ''),
                DynamicSubject: t.DynamicSubject,
                FromName: t.FromName,
                FromAddress: t.FromAddress,
                ReplyToAddress: t.ReplyToAddress,
                Charset: t.Charset,
                Category: t.Category,
                BodyHtml: t.BodyHtml,
                Stylesheet: t.Stylesheet,
                EditableHtml: t.EditableHtml,
                EditableCss: t.EditableCss,
                UseGoogleAnalytics: t.UseGoogleAnalytics,
                GoogleAnalyticsCampaignName: t.GoogleAnalyticsCampaignName,
                GoogleAnalyticsContent: t.GoogleAnalyticsContent,
                GoogleAnalyticsTerm: t.GoogleAnalyticsTerm,
                GoogleAnalyticsSource: t.GoogleAnalyticsSource,
                TrackingOptions: t.TrackingOptions,
                Tags: t.Tags,
                EventId: state.eventId || null,
                Segments: state.segmentId ? [{ SegmentId: state.segmentId }] : [],
                Lists: [],
                SurveyId: state.surveyId || null,
                SegmentId: state.segmentId ? null : undefined,
            };
        } else if (state.selectedIssueDetail) {
            const d = state.selectedIssueDetail;
            payload = {
                IssueId: state.issueId,
                Subject: buildSubject(d.Subject || ''),
                DynamicSubject: d.DynamicSubject,
                FromName: d.FromName,
                FromAddress: d.FromAddress,
                ReplyToAddress: d.ReplyToAddress,
                Charset: d.Charset,
                Culture: d.Culture,
                Category: d.Category,
                BodyHtml: d.BodyHtml,
                Stylesheet: d.Stylesheet,
                EditableHtml: d.EditableHtml,
                EditableCss: d.EditableCss,
                UseGoogleAnalytics: d.UseGoogleAnalytics,
                GoogleAnalyticsCampaignName: d.GoogleAnalyticsCampaignName,
                GoogleAnalyticsContent: d.GoogleAnalyticsContent,
                GoogleAnalyticsTerm: d.GoogleAnalyticsTerm,
                GoogleAnalyticsSource: d.GoogleAnalyticsSource,
                Status: d.Status,
                TrackingOptions: d.TrackingOptions,
                Tags: d.Tags,
                AccessControlList: d.AccessControlList,
                EventId: state.eventId || null,
                Segments: state.segmentId ? [{ SegmentId: state.segmentId }] : d.Segments,
                Lists: state.segmentId ? [] : d.Lists,
                SurveyId: state.surveyId || null,
                SegmentId: state.segmentId ? null : d.SegmentId,
            };
        } else {
            state.isLoading = false;
            return;
        }

        if (state.communicationType) {
            payload.EventCommunicationType = state.communicationType;
        }

        createIssueFromTemplate(payload)
            .then(async (issue) => {
                if (state.isParticipantStatusChangeIssue && state.eventId) {
                    await updateEventProperty(state.eventId, 'ParticipantStatusChangeIssueId', issue.IssueId);
                }
                document.location.href = '/Issues/' + issue.IssueId;
            })
            .catch(() => {
                bsAlert({ content: $Resources.ErrorCreatingIssue || 'Could not create issue', type: AlertTypes.Danger });
                state.isLoading = false;
            });
    };
</script>

<template>
    <header role="banner" class="p-lg-r p-lg-l">
        <div class="d-flex justify-content-between align-items-baseline">
            <h1>{{ $Resources.NewIssueFromTemplateHeading }}</h1>
        </div>
        <p class="m-lg-b">{{ $Resources.NewIssueFromTemplateIntroduction }}</p>
    </header>

    <tabs
        v-if="state.hasTemplatesScope !== undefined && state.hideFreeTemplates !== undefined"
        navClass="tabs list-unstyled m-lg-r m-lg-l"
        :selectedIndex="state.selectedTabIndex">
        <tab v-if="state.hasTemplatesScope" :title="$Resources.TemplatesTab" @tabClick="tabPickedByUser = true">
            <IssueTemplateList
                :onlyTemplates="true"
                :selectedId="state.issueId"
                @clickedIssueId="(id: string) => setIssueId(id, false, true)"
                @hasItems="updateCurrentTab" />
        </tab>
        <tab v-if="!state.hideFreeTemplates" :title="$Resources.FreeTemplatesTab" @tabClick="tabPickedByUser = true">
            <List
                itemId="TemplateId"
                displayAs="cards"
                :items="filteredGlobalTemplates"
                :toggleOptions="globalTemplateTags"
                :filterKeys="['Name', 'Description']"
                :loading="state.isLoading"
                :selectedId="state.issueId"
                :selectedToggle="state.currentGlobalTemplateTagId"
                :orderOptions="globalTemplateOrderOptions()"
                :orderByDirection="state.globalTemplateSortDirection"
                @update:selectedToggle="(newVal: string) => (state.currentGlobalTemplateTagId = newVal)"
                @update:order="updateGlobalTemplateOrder">
                <template #item="{ item }">
                    <div @click="setIssueId(item.TemplateId, true)" role="button">
                        <div class="card-media">
                            <img :src="item.ImageUrl" :alt="item.Name" />
                        </div>
                        <div class="card-content">
                            <h2 class="h4">
                                <a href="javascript:void(0)" role="button">{{ item.Name }}</a>
                            </h2>
                            <p>{{ item.Description }}</p>
                        </div>
                    </div>
                </template>
            </List>
        </tab>
        <tab :title="$Resources.MailingsAsTemplatesTab" @tabClick="tabPickedByUser = true">
            <IssueTemplateList :hideTemplates="true" :selectedId="state.issueId" @clickedIssueId="(id: string) => setIssueId(id)" />
        </tab>
    </tabs>

    <NewPreview :iframeSrc="state.previewSrc" :showImageToggle="true" @close="closePreview">
        <template #toolbar>
            <button v-if="state.hasIssuesScope" type="button" class="btn btn-cta p-lg-r p-lg-l" @click="startEditing" :disabled="state.isLoading">
                {{ $Resources.StartEditingBtn }}
                <i class="fa fa-chevron-right" aria-hidden="true"></i>
            </button>
        </template>
    </NewPreview>
</template>

